import React from 'react';
import { cn } from '../../../lib/cn';
import { Input } from '../atoms';

/**
 * Field, the mobile form unit: label, control, hint.
 *
 * CSS selector: `mobile/molecules.css` `.ds-field` (+ `--locked`), with
 * `.ds-field__label`, `.ds-field__control`, `.ds-field__hint`, and `.ds-lock`
 * for the trailing padlock.
 *
 * Doctrine: a locked field must never look editable, so `--locked` mutes the
 * value, suppresses the focus ring and reserves room for the padlock. Field
 * owns only the chrome around the control; the control styling stays with
 * `<Input>`, which is composed here rather than reimplemented.
 *
 * Pass a custom control through `children` for anything that is not a plain
 * text input.
 */
function LockGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export const Field = React.forwardRef(function Field(
  { className, label, hint, locked = false, htmlFor, children, inputProps, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('ds-field', locked && 'ds-field--locked', className)} {...rest}>
      {label ? (
        <label htmlFor={htmlFor} className="ds-field__label">
          {label}
        </label>
      ) : null}
      <div className="ds-field__control">
        {children || <Input id={htmlFor} big locked={locked} {...inputProps} />}
        {locked ? (
          <span className="ds-lock">
            <LockGlyph />
          </span>
        ) : null}
      </div>
      {hint ? <p className="ds-field__hint">{hint}</p> : null}
    </div>
  );
});

export default Field;
